/**
 * How a computed price was arrived at — the arithmetic build-up, once.
 *
 * `.docs/20-studio-portal-pages.md` §DRY watchlist: "both call
 * `catalog_compute_price` and render the breakdown through one `Breakdown` component
 * that billing.html's client table (19) also uses — check 19's implementation and
 * reuse."
 *
 * Session 19 built `components/client/QuoteBuildUp.tsx` and it is **the project half**
 * of this: it takes a work order's stored `lines` plus a `ProjectBreakdownView` and is
 * what the client's own quote fold prints. That is reused here rather than restated —
 * `Breakdown` for `kind: 'project'` is `QuoteBuildUpTable`, given the same fields.
 *
 * What 19 has no equivalent of is the **monthly** build-up, because no client page
 * shows one: `client/billing.html` prints the retainer's total and its blocks, not the
 * bundle band and the rounding. `MonthlyRows` below is that, and it is the same six
 * steps `MonthlyBreakdownSchema` declares, in its order.
 *
 * **The rounding line is shown only when it is non-zero** (`.docs/00-index.md` §6,
 * "Discount line display"), and the conversion line only when the client is not
 * billed in USD. Every other row is always there: a build-up that hides its zero
 * discount is one nobody can check.
 */

import {
  isZero,
  type ComputedPrice,
  type MonthlyBreakdownView,
  type WorkOrderLineView,
} from '@fareast/shared'

import { CustomBreakdownRows } from '../client/customLines.js'
import { QuoteBuildUpTable, type ProjectBreakdownView } from '../client/QuoteBuildUp.js'
import { useFormat } from '../../format/useFormat.js'
import { ScrollX } from '../portal/Layout.js'
import { TBody, THead, Table, Td, Th, Tr } from '../portal/Table.js'
import { Num } from '../portal/Text.js'

export type BreakdownProps = {
  readonly price: ComputedPrice
  /**
   * What the template line is called, for a project.
   *
   * `ProjectBreakdownSchema` carries the template's **amount** and not its name, and
   * the caller has one: `QuoteBuilder` chose the template from
   * `catalog_list_project_templates` and knows what it is called. Left out, the line
   * reads "Project template", which is true and is not what the quote should say.
   */
  readonly templateName?: string | undefined
}

export function Breakdown({ price, templateName }: BreakdownProps) {
  if (price.kind === 'project' && price.project !== null) {
    return (
      <QuoteBuildUpTable
        lines={projectLines(price.project, templateName)}
        breakdown={price.project}
        totalLabel="Quoted"
        total={price.total}
        discountLabel="Retainer client"
        /* Staff. `MonthlyTable` below states the tier on its own row for the same
         * reader, and `QuoteBuildUp` defaults it off for the client pages. */
        statesTier
      />
    )
  }

  /*
   * A package change prices two packages and the reader is deciding about the second
   * one, so the build-up shown is the **proposed** monthly. The before-and-after pair
   * is `computed.change` and is the caller's to print — `PackageBuilder` does, in one
   * line under this table.
   */
  const monthly = price.kind === 'package_change' ? (price.change?.proposed ?? null) : price.monthly
  if (monthly !== null) return <MonthlyTable monthly={monthly} />

  if (price.extra !== null) return <ExtraTable price={price} extra={price.extra} />

  return null
}

/**
 * The lines a *computed* project quote has, from the template and its add-ons.
 *
 * `QuoteBuildUpRows` takes stored `work_order_line` rows, because on the client's own
 * projects page there are some: the work order has been priced and the rows written.
 * A price that has only been **computed** has no rows at all — `catalog_compute_price`
 * writes nothing — and passing `lines={[]}` rendered the whole build-up as `null`,
 * because that component (correctly) treats "no lines" as "nothing to build up".
 *
 * So the lines are made here, from the two things the breakdown does carry. They are
 * the same figures `work_orders.price` would write: `breakdownToLines` in
 * `modules/work_orders/service.ts` is the API's own mapper from this answer to those
 * rows, and this is the display-only half of the same walk — the template first, then
 * each add-on in the order it was given, all USD Tier 1 list, which is what the
 * subtotal row below them is the sum of.
 */
function projectLines(project: ProjectBreakdownView, templateName?: string): WorkOrderLineView[] {
  return [
    {
      seq: 1,
      kind: 'template' as const,
      code: 'template',
      description: templateName ?? 'Project template',
      qty: 1,
      unit_amount: project.template,
      amount: project.template,
    },
    ...project.add_ons.map((addOn, index) => ({
      seq: index + 2,
      kind: 'add_on' as const,
      code: addOn.code,
      description: addOn.name,
      qty: 1,
      unit_amount: addOn.amount,
      amount: addOn.amount,
    })),
  ]
}

/* ------------------------------------------------------------------- monthly */

function MonthlyTable({ monthly }: { monthly: MonthlyBreakdownView }) {
  const format = useFormat()

  return (
    <ScrollX>
      <Table>
        <THead>
          <Tr>
            <Th>How it is worked out</Th>
            <Th right>Amount</Th>
          </Tr>
        </THead>
        <TBody>
          <Tr variant="sub">
            <Td>
              {monthly.block_count} {monthly.block_count === 1 ? 'block' : 'blocks'}, list
            </Td>
            <Td right>
              <Num value={monthly.list} />
            </Td>
          </Tr>
          <Tr variant="sub">
            <Td>Bundle discount, {format.percent(monthly.bundle_discount_bps)}</Td>
            <Td right>
              <Num value={monthly.bundle_discount} />
            </Td>
          </Tr>
          <Tr variant="sub">
            <Td>Market tier {monthly.tier.code}</Td>
            <Td right>
              <Num value={monthly.tier.amount} />
            </Td>
          </Tr>
          {monthly.converted === null ? null : (
            <Tr variant="sub">
              <Td>Converted to {monthly.currency}</Td>
              <Td right>
                <Num value={monthly.converted} />
              </Td>
            </Tr>
          )}
          {isZero(monthly.rounding) ? null : (
            <Tr variant="sub">
              <Td>Rounded to the nearest $10</Td>
              <Td right>
                <Num value={monthly.rounding} />
              </Td>
            </Tr>
          )}
          {/*
           * The blocks subtotal, each agreed line and their total — or nothing at all.
           * `total` is `blocks_total + custom_total` and every step above arrives at
           * `blocks_total`, so a package with custom lines needs the three rows or
           * this column stops adding up; a package without one renders byte for byte
           * what it rendered before. `components/client/customLines.tsx` owns them,
           * because all three renderers of `MonthlyBreakdownView` need the same three.
           */}
          <CustomBreakdownRows breakdown={monthly} labelSpan={1} />
          <Tr variant="total">
            <Td>A month</Td>
            <Td right>
              <Num value={monthly.total} lg />
            </Td>
          </Tr>
        </TBody>
      </Table>
    </ScrollX>
  )
}

/* --------------------------------------------------------------------- extra */

function ExtraTable({
  price,
  extra,
}: {
  price: ComputedPrice
  extra: NonNullable<ComputedPrice['extra']>
}) {
  const format = useFormat()

  return (
    <ScrollX>
      <Table>
        <THead>
          <Tr>
            <Th>What</Th>
            <Th right>Each</Th>
            <Th right>How many</Th>
            <Th right>Amount</Th>
          </Tr>
        </THead>
        <TBody>
          <Tr variant="sub">
            <Td>{format.qty(extra.qty, { one: extra.noun, other: `${extra.noun}s` })}</Td>
            <Td right>
              <Num value={extra.unit_amount} />
            </Td>
            <Td right>
              <Num value={extra.qty} />
            </Td>
            <Td right>
              <Num value={extra.amount} />
            </Td>
          </Tr>
          <Tr variant="total">
            <Td colSpan={3}>Total</Td>
            <Td right>
              <Num value={price.total} lg />
            </Td>
          </Tr>
        </TBody>
      </Table>
    </ScrollX>
  )
}
